/**
 * Theme-aware ECharts tooltip configuration using CSS custom properties.
 *
 * ECharts renders tooltips as DOM elements (not canvas), so CSS variables
 * defined in tokens.css are resolved at render time by the browser.
 * This ensures tooltips match the active theme (defaulted/smoked/spectre).
 */

type TooltipBaseOptions = {
  /** Override border color (e.g. accent color per chart). Falls back to --tooltip-border. */
  borderColor?: string;
  /** Override axis pointer color. Falls back to a neutral slate. */
  axisPointerColor?: string;
  /** Axis pointer type. Defaults to "line". */
  axisPointerType?: "line" | "shadow";
};

/**
 * Returns base ECharts tooltip config that adapts to the current theme.
 * Spread this into your chart's `tooltip` option and add your own `formatter`.
 */
export function getTooltipBase(options?: TooltipBaseOptions) {
  const border = options?.borderColor ?? "var(--tooltip-border)";
  const pointerColor = options?.axisPointerColor ?? "rgba(148, 163, 184, 0.4)";
  const pointerType = options?.axisPointerType ?? "line";

  return {
    trigger: "axis" as const,
    appendToBody: true,
    confine: true,
    backgroundColor: "transparent",
    borderColor: "transparent",
    borderWidth: 0,
    padding: 0,
    textStyle: { fontSize: 11, color: "var(--tooltip-fg)" },
    extraCssText: [
      "background: var(--tooltip-bg)",
      "color: var(--tooltip-fg)",
      `border: 1px solid ${border}`,
      "box-shadow: var(--tooltip-shadow)",
      "border-radius: 10px",
      "padding: 8px 12px",
      "z-index: 9990",
      "max-height: 70vh",
      "overflow-y: auto",
    ].join("; ") + ";",
    axisPointer: pointerType === "shadow"
      ? { type: "shadow" as const }
      : { type: "line" as const, lineStyle: { color: pointerColor, width: 1 } },
  };
}

/** Inline CSS for divider lines inside tooltip formatter HTML (e.g. "Total" row). */
export const TOOLTIP_DIVIDER_STYLE = "border-top: 1px solid var(--tooltip-divider)";
